import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SectionPanel from './SectionPanel';

export interface LinkedCompany {
  companyNumber: number;
  companyName: string;
  companyPresentation: string | null;
  isSystemCompany: boolean;
  website?: string;
  email?: string;
  phone?: string;
  experienceYears?: number;
  specialties?: string[];
  languages?: string[];
  geographicArea?: string;
  hourlyRate?: string;
  responseTime?: string;
  leadTime?: string;
}

interface LinkedCompaniesWidgetProps {
  companies: LinkedCompany[];
  /** Company numbers the person has an ongoing (not completed) project with — sorted first. */
  ongoingCompanyNumbers?: Set<number>;
  className?: string;
}

/**
 * "Dina Byggföretag" — the construction companies a private person is linked to,
 * as a compressed list (name + "Visa mera") that expands per company. Sits last
 * in the right column of the person start page, under "Bjud in Företag".
 *
 * Sorting (decided): companies with an ongoing (not completed) project first,
 * then alphabetically by company name.
 */
export default function LinkedCompaniesWidget({ companies, ongoingCompanyNumbers, className }: LinkedCompaniesWidgetProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = (companyNumber: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(companyNumber)) next.delete(companyNumber);
      else next.add(companyNumber);
      return next;
    });
  };

  const realCompanies = companies
    .filter(c => !c.isSystemCompany)
    .sort((a, b) => {
      const aOngoing = ongoingCompanyNumbers?.has(a.companyNumber) ? 1 : 0;
      const bOngoing = ongoingCompanyNumbers?.has(b.companyNumber) ? 1 : 0;
      if (aOngoing !== bOngoing) return bOngoing - aOngoing;
      return a.companyName.localeCompare(b.companyName, 'sv');
    });
  const isSingleCompany = realCompanies.length === 1;

  return (
    <SectionPanel title={t('personHome.linkedCompanies.title')} fillHeight={false} className={className}>
      {realCompanies.length === 0 ? (
        <div className="text-center text-muted py-4" data-testid="linked-companies-empty">
          <i className="feather-briefcase d-block mb-3" style={{ fontSize: '2.5rem', opacity: 0.5 }}></i>
          <p className="mb-1 fw-semibold">{t('personHome.linkedCompanies.empty.title')}</p>
          <p className="mb-0 small">{t('personHome.linkedCompanies.empty.body')}</p>
        </div>
      ) : (
        <ul className="list-unstyled mb-0">
          {realCompanies.map(company => {
            const isOpen = isSingleCompany || expanded.has(company.companyNumber);
            const panelId = `linked-company-${company.companyNumber}`;
            return (
              <li
                key={company.companyNumber}
                className="border rounded mb-2 overflow-hidden"
                data-testid={`linked-company-item-${company.companyNumber}`}
              >
                {isSingleCompany ? (
                  <div className="w-100 d-flex align-items-center gap-2 text-start px-3 py-2" style={{ minHeight: 44 }}>
                    <span className="flex-grow-1 text-truncate fw-semibold">{company.companyName}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn expandable-row-toggle w-100 d-flex align-items-center gap-2 text-start px-3 py-2 border-0 rounded-0"
                    style={{ minHeight: 44 }}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(company.companyNumber)}
                  >
                    <span className="flex-grow-1 text-truncate fw-semibold">{company.companyName}</span>
                    <span className="text-nowrap small text-primary d-flex align-items-center gap-1">
                      {isOpen ? t('personHome.linkedCompanies.showLess') : t('personHome.linkedCompanies.showMore')}
                      <i className={`feather-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
                    </span>
                  </button>
                )}

                <div id={panelId} role="region" hidden={!isOpen} className="px-3 pb-3">
                  <div className="mb-3">
                    <span className="badge bg-success">
                      <i className="feather-check me-1" style={{ fontSize: 10 }}></i>
                      {t('personHome.yourContractor')}
                    </span>
                  </div>

                  {/* Contact info */}
                  {(company.phone || company.email || company.website) && (
                    <div className="d-flex flex-column gap-2 mb-3 small">
                      {company.phone && (
                        <span className="text-muted text-break"><i className="feather-phone me-1" style={{ fontSize: 13 }}></i>{company.phone}</span>
                      )}
                      {company.email && (
                        <span className="text-muted text-break"><i className="feather-mail me-1" style={{ fontSize: 13 }}></i>{company.email}</span>
                      )}
                      {company.website && (
                        <span className="text-muted text-break"><i className="feather-globe me-1" style={{ fontSize: 13 }}></i>{company.website}</span>
                      )}
                    </div>
                  )}

                  {/* Specialties */}
                  {(company.specialties || []).length > 0 && (
                    <div className="mb-3">
                      <small className="fw-semibold text-muted d-block mb-1">{t('marketplace.provider.specialties', 'Specialistområden')}</small>
                      <div className="d-flex flex-wrap gap-1">
                        {(company.specialties || []).map((s, i) => (
                          <span key={i} className="badge bg-light text-dark border">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Practical info — labels + values, empty fields omitted */}
                  {((company.experienceYears || 0) > 0 || company.hourlyRate || company.geographicArea || company.responseTime || company.leadTime || (company.languages || []).length > 0) && (
                    <div className="bg-light rounded p-3 mb-3">
                      <small className="fw-semibold text-muted d-block mb-2">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</small>
                      <div className="row g-2">
                        {(company.experienceYears || 0) > 0 && (
                          <div className="col-12">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-award text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.experience', 'Erfarenhet')}</div>
                                <div className="fw-semibold small">{t('personHome.experienceYears', { count: company.experienceYears })}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {company.hourlyRate && (
                          <div className="col-12">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-tag text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.hourlyRate', 'Timarvode')}</div>
                                <div className="fw-semibold small">{company.hourlyRate}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {company.responseTime && (
                          <div className="col-12">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-clock text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.responseTime', 'Svarstid')}</div>
                                <div className="fw-semibold small">{company.responseTime}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {company.geographicArea && (
                          <div className="col-12">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-map-pin text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.geographicArea', 'Område')}</div>
                                <div className="fw-semibold small">{company.geographicArea}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {(company.languages || []).length > 0 && (
                          <div className="col-12">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-globe text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.languages', 'Språk')}</div>
                                <div className="fw-semibold small">{(company.languages || []).join(', ')}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {company.leadTime && (
                          <div className="col-12">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-calendar text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.leadTime', 'Ledtid')}</div>
                                <div className="fw-semibold small">{company.leadTime}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Company presentation */}
                  {company.companyPresentation ? (
                    <div className="border rounded p-3 bg-white">
                      <small className="fw-semibold text-muted d-block mb-2">{t('settings.companyDetails.companyPresentation', 'Företagspresentation')}</small>
                      <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: company.companyPresentation }} />
                    </div>
                  ) : (
                    <p className="text-muted small mb-0">{t('personHome.noPresentation')}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </SectionPanel>
  );
}
